<!-- Ported from the Polaris kit: src/product/components/charts/RatingDistribution.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase 7.4, 29/09): the rating names come in as `names` ({ 1: …, 5: … }, the names without the emoji,
     POLARIS_REPORTS.RATING_NAMES (R3-15 = B), in pt_BR: Ruim … Excelente, same order as the kit's five) and
     "No answers yet." / the default label go through i18n.
     The colours are the kit's (success, a mix, muted, warning, destructive): only the report changes, the woot's
     CSAT_RATINGS.color stays for the widget and the survey. The bar widths are a runtime value, so they stay inline
     styles, as in the kit. -->
<script setup>
// How the answers of a satisfaction survey split over five ratings: one bar cut into five, and the same numbers in words under it, so no
// share is told by color alone. Loading shows where things will be; with no answers the bar is empty and says so.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';

const props = defineProps({
  counts: { type: Object, default: () => ({}) }, // { 1: n, 2: n, 3: n, 4: n, 5: n }
  names: { type: Object, default: () => ({}) }, // { 1: 'Poor', …, 5: 'Excellent' }
  label: { type: String, default: '' },
  loading: Boolean,
});
const { t } = useI18n();
const ratings = [
  { value: 5, color: 'var(--polaris-success-foreground)' },
  {
    value: 4,
    color:
      'color-mix(in oklch, var(--polaris-success-foreground) 50%, var(--polaris-muted-foreground))',
  },
  { value: 3, color: 'var(--polaris-muted-foreground)' },
  { value: 2, color: 'var(--polaris-warning-foreground)' },
  { value: 1, color: 'var(--polaris-destructive)' },
];
const total = computed(() =>
  ratings.reduce((sum, rating) => sum + (props.counts[rating.value] ?? 0), 0)
);
const rows = computed(() =>
  ratings.map(rating => {
    const count = props.counts[rating.value] ?? 0;
    return {
      ...rating,
      name: props.names[rating.value] ?? String(rating.value),
      count,
      share: total.value ? Math.round((count / total.value) * 100) : 0,
    };
  })
);
const summary = computed(() =>
  rows.value.map(row => `${row.name} ${row.share}%`).join(', ')
);
</script>

<!-- eslint-disable vue/no-static-inline-styles -->
<template>
  <Card
    class="gap-0 py-4"
    data-rating-distribution
    :aria-busy="loading || undefined"
  >
    <CardContent class="grid gap-4 px-4">
      <p class="type-polaris-label text-polaris-muted-foreground">
        {{ label || t('CSAT_REPORTS.METRIC.RATING_DISTRIBUTION') }}
      </p>
      <template v-if="loading">
        <Skeleton class="h-6 w-full rounded-full" />
        <div class="flex flex-wrap gap-x-6 gap-y-2">
          <Skeleton v-for="n in 5" :key="n" class="h-4 w-20" />
        </div>
      </template>
      <template v-else>
        <div
          v-if="total"
          class="flex h-6 w-full gap-0.5 overflow-hidden rounded-full"
          role="img"
          :aria-label="summary"
        >
          <span
            v-for="row in rows.filter(r => r.count)"
            :key="row.value"
            class="h-full first:rounded-s-full last:rounded-e-full"
            :style="{ width: `${row.share}%`, backgroundColor: row.color }"
          />
        </div>
        <div v-else class="h-6 w-full rounded-full bg-polaris-muted" />
        <p
          v-if="!total"
          class="type-polaris-caption text-polaris-muted-foreground"
        >
          {{ t('POLARIS_REPORTS.RATINGS.NO_ANSWERS') }}
        </p>
        <ul class="m-0 flex list-none flex-wrap gap-x-6 gap-y-2 p-0">
          <li
            v-for="row in rows"
            :key="row.value"
            class="flex items-center gap-2 type-polaris-caption"
          >
            <span
              class="size-2.5 shrink-0 rounded-full"
              :style="{ backgroundColor: row.color }"
              aria-hidden="true"
            />
            <span>{{ row.name }}</span>
            <span class="text-polaris-foreground">
              {{ t('POLARIS_REPORTS.TREND.SHORT', { value: row.share }) }}
            </span>
            <span class="text-polaris-muted-foreground">
              {{ t('POLARIS_REPORTS.RATINGS.COUNT', { count: row.count }) }}
            </span>
          </li>
        </ul>
      </template>
    </CardContent>
  </Card>
</template>
